import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { Gift, User as UserIcon, Edit2, Check, X, Loader2, Crown, ChevronLeft, ChevronRight, Eye, EyeOff, Lock } from 'lucide-react';
import PointsAPI from '../services/pointsApi';
import PaymentAPI from '../services/paymentApi';
import ProfileAPI from '../services/profileApi';
import authApi from '../services/authApi';
import AlertModal from '../components/common/AlertModal';

const SectionHeader = styled.div`
  margin-bottom: 32px;
  padding-left: 24px;

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #475569;
  }
`;

const PageContainer = styled.div`
  padding: 40px 24px;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
`;

const CardHeader = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: #495057;
`;

const EditableInfoRow = styled(InfoRow)`
  position: relative;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: #495057;
  }
`;

const SaveButton = styled.button`
  background: ${props => props.disabled ? '#6c757d' : '#28a745'};
  color: #fff;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-left: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? '#6c757d' : '#218838'};
  }
`;

const CancelButton = styled.button`
  background: ${props => props.disabled ? '#adb5bd' : '#6c757d'};
  color: #fff;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-left: 4px;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? '#adb5bd' : '#5a6268'};
  }
`;

const NicknameInput = styled.input`
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  color: #495057;
  background: #fff;
  width: 120px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
`;

const PhoneInput = styled.input`
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  color: #495057;
  background: #fff;
  width: 180px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
`;

const JobInput = styled.input`
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  color: #495057;
  background: #fff;
  width: 200px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
`;

const CitySelect = styled.select`
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  color: #495057;
  background: #fff;
  width: 180px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
`;

const SpinningIcon = styled(Loader2)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Badge = styled.span`
  background: #f1f3f5;
  color: #495057;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
`;

const VipBadge = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const EntriesContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
`;

const PaginationButton = styled.button`
  background: ${props => props.active ? '#667eea' : '#fff'};
  color: ${props => props.active ? '#fff' : '#495057'};
  border: 1px solid ${props => props.active ? '#667eea' : '#e9ecef'};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  min-width: 32px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => props.active ? '#5568d3' : '#f8f9fa'};
    border-color: ${props => props.active ? '#5568d3' : '#667eea'};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const PaginationInfo = styled.span`
  font-size: 12px;
  color: #6c757d;
  margin: 0 8px;
`;

// 中国主要城市列表（按照行政级别和经济影响力排序，已去重）
const CITIES_LIST = [
  // 一线城市
  '北京', '上海', '广州', '深圳',
  // 直辖市
  '重庆', '天津',
  // 副省级城市及新一线城市
  '成都', '杭州', '武汉', '西安', '南京', '苏州', '青岛', '大连', '宁波', '厦门',
  '长沙', '郑州', '无锡', '佛山', '东莞', '福州', '合肥', '济南', '石家庄',
  // 省会城市
  '太原', '呼和浩特', '长春', '哈尔滨', '南昌', '昆明', '乌鲁木齐', '兰州',
  '银川', '西宁', '拉萨', '南宁', '海口',
  // 经济发达地级市
  // 广东省
  '珠海', '中山', '惠州', '汕头', '江门', '肇庆', '湛江', '茂名', '清远', '韶关', '阳江',
  // 江苏省
  '南通', '扬州', '泰州', '盐城', '镇江', '常州', '徐州', '连云港',
  // 浙江省
  '嘉兴', '湖州', '绍兴', '台州', '温州', '金华', '衢州', '舟山',
  // 福建省
  '泉州', '莆田', '漳州', '宁德', '龙岩', '三明', '南平',
  // 江西省
  '九江', '上饶', '赣州', '吉安', '宜春',
  // 湖北省
  '宜昌', '襄阳', '荆州', '黄冈', '孝感', '十堰',
  // 河南省
  '洛阳', '南阳', '商丘', '周口', '驻马店', '信阳',
  // 湖南省
  '株洲', '湘潭', '衡阳', '邵阳', '岳阳', '常德', '郴州',
  // 四川省
  '绵阳', '德阳', '宜宾', '南充', '自贡', '乐山', '泸州',
  // 广西省
  '桂林', '柳州', '北海', '梧州', '玉林',
  // 贵州省
  '遵义', '六盘水', '毕节',
  // 云南省
  '曲靖', '玉溪', '大理', '丽江',
  // 海南省
  '三亚', '儋州',
  // 其他类别
  '其他'
];

// 去重处理
const CITIES = [...new Set(CITIES_LIST)];

// 将reason中的英文单词转换为中文
function translateReason(reason) {
  if (!reason) return reason;
  // 将常见的英文订阅周期转换为中文
  let translated = reason
    .replace(/订阅month充值/gi, '订阅月卡充值')
    .replace(/订阅week充值/gi, '订阅周卡充值')
    .replace(/订阅quarter充值/gi, '订阅季卡充值')
    .replace(/month/gi, '月')
    .replace(/week/gi, '周')
    .replace(/quarter/gi, '季度');
  return translated;
}

// 将reason转换为清晰的中文描述
function formatReason(reason) {
  if (!reason) return '未知';
  
  // 先处理常见的纯英文reason值
  if (reason === 'initial_bonus') return '新用户赠送';
  if (reason === 'recharge') return '充值';
  if (reason === 'referral_success') return '邀请成功奖励';
  if (reason === 'resume_diagnosis') return '简历诊断';
  if (reason === 'interview_prep') return '面试押题';
  if (reason === 'mock_interview') return '模拟面试';
  if (reason === 'meeting_assistant') return '会议助手';
  
  // 转换中英文混杂的内容（如"订阅month充值"）
  let translated = translateReason(reason);
  
  // 如果翻译后的内容与原始内容不同，说明已经翻译过了，直接返回
  if (translated !== reason) {
    return translated;
  }
  
  // 其他情况返回原值（可能是中文或者未知格式）
  return reason;
}

// 修改密码按钮样式
const ChangePasswordButton = styled.button`
  background-color: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: #e9ecef;
    border-color: #adb5bd;
  }
`;

// 修改密码弹窗样式
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #495057;
  }
`;

const PasswordInputContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  padding-right: 36px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  color: #495057;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
`;

const EyeButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: #495057;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
`;

const CancelActionButton = styled.button`
  background-color: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e9ecef;
    border-color: #adb5bd;
  }
`;

const SubmitActionButton = styled.button`
  background-color: ${props => props.disabled ? '#6c757d' : '#007bff'};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ToastMessage = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  animation: slideIn 0.3s ease-out, slideOut 0.3s ease-in 2.7s forwards;
  display: flex;
  align-items: center;
  gap: 10px;
  
  ${props => props.type === 'success' && `
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
  `}
  
  ${props => props.type === 'error' && `
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
  `}
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
  
  &::before {
    content: ${props => props.type === 'success' ? '"✓"' : '"✗"'};
    font-size: 18px;
    font-weight: bold;
  }
`;

const AccountSettingsPage = () => {
  const { user, updateUser } = useAuth();
  const [userProfile, setUserProfile] = useState({
    nickname: '',
    email: '',
    contact_info: '',
    expect_job: '',
    expect_city: '',
    login_type: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [nicknameValue, setNicknameValue] = useState('');
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [isEditingExpectedJob, setIsEditingExpectedJob] = useState(false);
  const [expectedJobValue, setExpectedJobValue] = useState('');
  const [isEditingExpectedCity, setIsEditingExpectedCity] = useState(false);
  const [expectedCityValue, setExpectedCityValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inviterId = useMemo(() => user?.user_id || null, [user]);
  
  // 加载用户资料
  const loadUserProfile = async () => {
    if (!user?.id) {
      console.log('loadUserProfile - 用户ID不存在，跳过API调用');
      return;
    }
    
    try {
      setIsLoading(true);
      // 确保传递数字类型的user_id
      const profileData = await ProfileAPI.getProfile(user.id);
      setUserProfile(profileData);
      
      // 初始化编辑表单的值
      setNicknameValue(profileData.nickname || '');
      setPhoneValue(profileData.contact_info || '');
      setExpectedJobValue(profileData.expect_job || '');
      setExpectedCityValue(profileData.expect_city || '');
    } catch (error) {
      console.error('加载用户资料失败:', error);
      showToast('加载用户资料失败，请刷新页面重试', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // 页面加载时获取用户资料
  useEffect(() => {
    loadUserProfile();
  }, [user?.id]);
  const [points, setPoints] = useState({ balance: 0, entries: [], total_earned: 0, total_spent: 0 });
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [vipInfo, setVipInfo] = useState({ vipLevel: 'L0', isVip: false, subscription: null });
  const [currentPage, setCurrentPage] = useState(1);
  
  // 修改密码相关状态
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 成功提示弹窗状态
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // Toast 通知状态
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('success');
  
  // 显示 Toast 通知
  const showToast = (message, type = 'success') => {
    setToastType(type);
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };
  
  // 密码可见性状态
  const [isCurrentPasswordVisible, setIsCurrentPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const itemsPerPage = 10; // 每页显示10条记录

  const handleEditNickname = () => {
    setIsEditingNickname(true);
    setNicknameValue(user?.name || '');
  };

  const handleSaveNickname = async () => {
    try {
      if (!nicknameValue.trim()) {
        showToast('昵称不能为空', 'error');
        return;
      }
      
      setIsSaving(true);
      // 确保传递数字类型的user_id
      const updatedProfile = await ProfileAPI.updateProfile(user.id, {
        nickname: nicknameValue.trim()
      });
      setUserProfile(updatedProfile);
      
      // 同步更新全局用户状态，使左下角功能栏立即显示新昵称
      await updateUser({ name: nicknameValue.trim() });
      
      setIsEditingNickname(false);
      showToast('昵称更新成功！', 'success');
    } catch (error) {
      showToast('更新失败，请重试', 'error');
      console.error('更新昵称失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingNickname(false);
    setNicknameValue(userProfile.nickname || '');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveNickname();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleEditPhone = () => {
    setIsEditingPhone(true);
    setPhoneValue(user?.phone || '');
  };

  const handleSavePhone = async () => {
    try {
      setIsSaving(true);
      // 确保传递数字类型的user_id
      const updatedProfile = await ProfileAPI.updateProfile(Number(user.id), {
        contact_info: phoneValue.trim()
      });
      setUserProfile(updatedProfile);
      setIsEditingPhone(false);
      showToast('联系电话更新成功！', 'success');
    } catch (error) {
      showToast('更新失败，请重试', 'error');
      console.error('更新联系电话失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelPhoneEdit = () => {
    setIsEditingPhone(false);
    setPhoneValue(userProfile.contact_info || '');
  };

  const handleKeyPressPhone = (e) => {
    if (e.key === 'Enter') {
      handleSavePhone();
    } else if (e.key === 'Escape') {
      handleCancelPhoneEdit();
    }
  };

  // 期望职位处理函数
  const handleEditExpectedJob = () => {
    setIsEditingExpectedJob(true);
    setExpectedJobValue(user?.expect_job || '');
  };

  const handleSaveExpectedJob = async () => {
    try {
      setIsSaving(true);
      // 确保传递数字类型的user_id
      const updatedProfile = await ProfileAPI.updateProfile(Number(user.id), {
        expect_job: expectedJobValue.trim()
      });
      setUserProfile(updatedProfile);
      setIsEditingExpectedJob(false);
      showToast('期望职位更新成功！', 'success');
    } catch (error) {
      showToast('更新失败，请重试', 'error');
      console.error('更新期望职位失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelExpectedJobEdit = () => {
    setIsEditingExpectedJob(false);
    setExpectedJobValue(userProfile.expect_job || '');
  };

  const handleKeyPressExpectedJob = (e) => {
    if (e.key === 'Enter') {
      handleSaveExpectedJob();
    } else if (e.key === 'Escape') {
      handleCancelExpectedJobEdit();
    }
  };

  // 期望城市处理函数
  const handleEditExpectedCity = () => {
    setIsEditingExpectedCity(true);
    setExpectedCityValue(user?.expect_city || '');
  };

  const handleSaveExpectedCity = async () => {
    try {
      setIsSaving(true);
      // 确保传递数字类型的user_id
      const updatedProfile = await ProfileAPI.updateProfile(Number(user.id), {
        expect_city: expectedCityValue
      });
      setUserProfile(updatedProfile);
      setIsEditingExpectedCity(false);
      showToast('期望城市更新成功！', 'success');
    } catch (error) {
      showToast('更新失败，请重试', 'error');
      console.error('更新期望城市失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelExpectedCityEdit = () => {
    setIsEditingExpectedCity(false);
    setExpectedCityValue(userProfile.expect_city || '');
  };

  const handleCityChange = (e) => {
    setExpectedCityValue(e.target.value);
  };
  
  // 修改密码相关函数
  const handleOpenPasswordModal = () => {
    setShowPasswordModal(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
  };
  
  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
  };
  
  // 密码验证函数
  const validateForm = () => {
    const newErrors = {};
    
    if (!currentPassword) {
      newErrors.currentPassword = '请输入当前密码';
    }
    
    if (!newPassword) {
      newErrors.newPassword = '请输入新密码';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = '新密码长度不能少于6位';
    } else if (newPassword === currentPassword) {
      newErrors.newPassword = '新密码不能与当前密码相同';
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = '请确认新密码';
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = '两次输入的密码不一致';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // 修改密码API调用函数
  const handleChangePassword = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      setErrors({ submit: '' });
      
      const response = await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      if (!response.success) {
        throw new Error(response.message || '修改密码失败');
      }
      
      // 使用AlertModal显示成功提示
      setShowSuccessModal(true);
      handleClosePasswordModal();
    } catch (error) {
      setErrors({ submit: error.message || '修改密码失败，请重试' });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!inviterId) return;
      try {
        setLoadingPoints(true);
        const data = await PointsAPI.getHistory(inviterId);
        setPoints({
          balance: data.balance || 0,
          entries: data.entries || [],
          total_earned: data.total_earned || 0,
          total_spent: data.total_spent || 0,
        });
        // 重置页码为第一页
        setCurrentPage(1);
      } catch (e) {
        console.warn('加载积分失败', e);
      } finally {
        setLoadingPoints(false);
      }
    };
    load();
  }, [inviterId]);

  // 获取VIP订阅信息
  useEffect(() => {
    const loadVipInfo = async () => {
      if (!inviterId) return;
      try {
        const response = await PaymentAPI.getUserSubscription(inviterId);
        if (response.success) {
          setVipInfo({
            vipLevel: response.vip_level || 'L0',
            isVip: response.is_vip || false,
            subscription: response.subscription
          });
        }
      } catch (error) {
        console.error('获取VIP信息失败:', error);
      }
    };
    loadVipInfo();

    // 监听VIP信息更新事件
    const handleVipInfoUpdate = () => {
      loadVipInfo();
    };
    
    window.addEventListener('vipInfoUpdated', handleVipInfoUpdate);
    
    return () => {
      window.removeEventListener('vipInfoUpdated', handleVipInfoUpdate);
    };
  }, [inviterId]);

  

  return (
    <>
      <SectionHeader>
        <h2>账户设置</h2>
        <p>管理您的账户设置和偏好</p>
      </SectionHeader>

      <PageContainer>
        {/* 账户信息 */}
        <Card>
            <CardHeader>
              <UserIcon size={16} /> 账户信息
            </CardHeader>
            <EditableInfoRow>
              <span>昵称</span>
              {isEditingNickname ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <NicknameInput
                    value={nicknameValue}
                    onChange={(e) => setNicknameValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="请输入昵称"
                    maxLength={20}
                    autoFocus
                  />
                  <SaveButton onClick={handleSaveNickname} disabled={isSaving}>
                    {isSaving ? <SpinningIcon size={12} /> : <Check size={12} />}
                  </SaveButton>
                  <CancelButton onClick={handleCancelEdit} disabled={isSaving}>
                    <X size={12} />
                  </CancelButton>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{userProfile.nickname || '未设置'}</span>
                  <EditButton onClick={handleEditNickname}>
                    <Edit2 size={14} />
                  </EditButton>
                </div>
              )}
            </EditableInfoRow>
            <InfoRow>
              <span>邮箱</span>
              <span>{userProfile.email || '未提供'}</span>
            </InfoRow>
            <EditableInfoRow>
              <span>联系方式</span>
              {isEditingPhone ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneInput
                    type="tel"
                    value={phoneValue}
                    onChange={(e) => setPhoneValue(e.target.value)}
                    onKeyDown={handleKeyPressPhone}
                    placeholder="请输入联系电话"
                    maxLength={11}
                    autoFocus
                  />
                  <SaveButton onClick={handleSavePhone} disabled={isSaving}>
                    {isSaving ? <SpinningIcon size={12} /> : <Check size={12} />}
                  </SaveButton>
                  <CancelButton onClick={handleCancelPhoneEdit} disabled={isSaving}>
                    <X size={12} />
                  </CancelButton>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{userProfile.contact_info || '未提供'}</span>
                  <EditButton onClick={handleEditPhone}>
                    <Edit2 size={14} />
                  </EditButton>
                </div>
              )}
            </EditableInfoRow>
            <InfoRow>
              <span>登录方式</span>
              <span><Badge>{userProfile.login_type || '未知'}</Badge></span>
            </InfoRow>
            <InfoRow>
              <span>登录密码</span>
              <ChangePasswordButton onClick={handleOpenPasswordModal}>
                <Lock size={14} />
                修改密码
              </ChangePasswordButton>
            </InfoRow>
            <InfoRow>
              <span>会员类型</span>
              <span>
                {vipInfo.isVip ? (
                  <VipBadge>
                    <Crown size={14} style={{ color: '#f59e0b' }} />
                    {vipInfo.vipLevel === 'L1' ? '会员版 - 月卡' : vipInfo.vipLevel === 'L2' ? '会员版 - 季卡' : '会员'}
                  </VipBadge>
                ) : (
                  <Badge>体验版</Badge>
                )}
              </span>
            </InfoRow>
            {vipInfo.isVip && vipInfo.subscription && (
              <InfoRow>
                <span>到期时间</span>
                <span style={{ color: vipInfo.subscription.is_active ? '#16a34a' : '#dc2626', fontWeight: '500' }}>
                  {vipInfo.subscription.end_date 
                    ? new Date(vipInfo.subscription.end_date).toLocaleDateString('zh-CN', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : '未知'}
                  {vipInfo.subscription.days_remaining !== undefined && vipInfo.subscription.days_remaining > 0 && (
                    <span style={{ marginLeft: '8px', color: '#6c757d', fontSize: '13px' }}>
                      （剩余 {vipInfo.subscription.days_remaining} 天）
                    </span>
                  )}
                </span>
              </InfoRow>
            )}
            <EditableInfoRow>
              <span>期望职位</span>
              {isEditingExpectedJob ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <JobInput
                    value={expectedJobValue}
                    onChange={(e) => setExpectedJobValue(e.target.value)}
                    onKeyDown={handleKeyPressExpectedJob}
                    placeholder="请输入期望职位"
                    maxLength={50}
                    autoFocus
                  />
                  <SaveButton onClick={handleSaveExpectedJob} disabled={isSaving}>
                    {isSaving ? <SpinningIcon size={12} /> : <Check size={12} />}
                  </SaveButton>
                  <CancelButton onClick={handleCancelExpectedJobEdit} disabled={isSaving}>
                    <X size={12} />
                  </CancelButton>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{userProfile.expect_job || '未提供'}</span>
                  <EditButton onClick={handleEditExpectedJob}>
                    <Edit2 size={14} />
                  </EditButton>
                </div>
              )}
            </EditableInfoRow>
            <EditableInfoRow>
              <span>期望城市</span>
              {isEditingExpectedCity ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CitySelect
                    value={expectedCityValue}
                    onChange={handleCityChange}
                    autoFocus
                  >
                    <option value="">请选择城市</option>
                    {CITIES.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </CitySelect>
                  <SaveButton onClick={handleSaveExpectedCity} disabled={isSaving}>
                    {isSaving ? <SpinningIcon size={12} /> : <Check size={12} />}
                  </SaveButton>
                  <CancelButton onClick={handleCancelExpectedCityEdit} disabled={isSaving}>
                    <X size={12} />
                  </CancelButton>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{userProfile.expect_city || '未提供'}</span>
                  <EditButton onClick={handleEditExpectedCity}>
                    <Edit2 size={14} />
                  </EditButton>
                </div>
              )}
            </EditableInfoRow>
          </Card>

          {/* 积分与充值记录 */}
          <Card>
            <CardHeader>
              <Gift size={16} /> 积分与充值记录
            </CardHeader>
            <InfoRow>
              <span>可用积分</span>
              <span style={{ color: '#28a745', fontWeight: '600' }}>{points.balance} 积分</span>
            </InfoRow>
            <InfoRow>
              <span>累计获得</span>
              <span style={{ color: '#16a34a' }}>+{points.total_earned} 积分</span>
            </InfoRow>
            <InfoRow>
              <span>累计消耗</span>
              <span style={{ color: '#dc2626' }}>-{points.total_spent} 积分</span>
            </InfoRow>
            <div style={{ marginTop: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>积分明细</div>
              {loadingPoints && <div style={{ fontSize: '13px', color: '#6c757d' }}>加载中...</div>}
              {!loadingPoints && points.entries.length === 0 && (
                <div style={{ fontSize: '13px', color: '#6c757d' }}>暂无记录</div>
              )}
              {!loadingPoints && points.entries.length > 0 && (() => {
                const totalPages = Math.ceil(points.entries.length / itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const currentEntries = points.entries.slice(startIndex, endIndex);
                
                return (
                  <>
                    <EntriesContainer>
                      {currentEntries.map((e) => (
                        <div key={e.id} style={{ fontSize: '13px', color: '#495057', marginBottom: '4px' }}>
                          <span>{new Date(e.created_at).toLocaleString()} [{formatReason(e.reason)}]</span>
                          <span style={{ float: 'right', color: e.change >= 0 ? '#16a34a' : '#dc2626' }}>{e.change > 0 ? `+${e.change}` : e.change}积分</span>
                        </div>
                      ))}
                    </EntriesContainer>
                    {totalPages > 1 && (() => {
                      // 智能分页显示逻辑
                      const getPageNumbers = () => {
                        const pages = [];
                        const showEllipsis = totalPages > 7;
                        
                        if (!showEllipsis) {
                          // 如果总页数少于等于7，显示所有页码
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // 始终显示第一页
                          pages.push(1);
                          
                          if (currentPage <= 4) {
                            // 当前页在前4页，显示前5页
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          } else if (currentPage >= totalPages - 3) {
                            // 当前页在后4页，显示后5页
                            pages.push('ellipsis');
                            for (let i = totalPages - 4; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // 当前页在中间，显示当前页前后各1页
                            pages.push('ellipsis');
                            pages.push(currentPage - 1);
                            pages.push(currentPage);
                            pages.push(currentPage + 1);
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          }
                        }
                        
                        return pages;
                      };
                      
                      const pageNumbers = getPageNumbers();
                      
                      return (
                        <PaginationContainer>
                          <PaginationButton
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            title="上一页"
                          >
                            <ChevronLeft size={14} />
                          </PaginationButton>
                          {pageNumbers.map((page, index) => {
                            if (page === 'ellipsis') {
                              return (
                                <span key={`ellipsis-${index}`} style={{ color: '#6c757d', padding: '0 4px' }}>
                                  ...
                                </span>
                              );
                            }
                            return (
                              <PaginationButton
                                key={page}
                                active={currentPage === page}
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </PaginationButton>
                            );
                          })}
                          <PaginationButton
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            title="下一页"
                          >
                            <ChevronRight size={14} />
                          </PaginationButton>
                          <PaginationInfo>
                            第 {currentPage} / {totalPages} 页，共 {points.entries.length} 条
                          </PaginationInfo>
                        </PaginationContainer>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          </Card>
          
          {/* 修改密码弹窗 */}
          {showPasswordModal && (
            <ModalOverlay onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleClosePasswordModal();
              }
            }}>
              <ModalContainer>
                <ModalHeader>
                  <h3>
                    <Lock size={18} />
                    修改登录密码
                  </h3>
                  <CloseButton onClick={handleClosePasswordModal}>&times;</CloseButton>
                </ModalHeader>
                
                {errors.submit && <ErrorText>{errors.submit}</ErrorText>}
                
                <div>
                  <Label htmlFor="currentPassword">当前密码</Label>
                  <PasswordInputContainer>
                    <PasswordInput
                      id="currentPassword"
                      type={isCurrentPasswordVisible ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="请输入当前密码"
                      disabled={isSubmitting}
                    />
                    <EyeButton onClick={() => setIsCurrentPasswordVisible(!isCurrentPasswordVisible)}>
                      {isCurrentPasswordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </EyeButton>
                  </PasswordInputContainer>
                  {errors.currentPassword && <ErrorText>{errors.currentPassword}</ErrorText>}
                </div>
                
                <div>
                  <Label htmlFor="newPassword">新密码</Label>
                  <PasswordInputContainer>
                    <PasswordInput
                      id="newPassword"
                      type={isNewPasswordVisible ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="请输入新密码"
                      disabled={isSubmitting}
                    />
                    <EyeButton onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)}>
                      {isNewPasswordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </EyeButton>
                  </PasswordInputContainer>
                  {errors.newPassword && <ErrorText>{errors.newPassword}</ErrorText>}
                </div>
                
                <div>
                  <Label htmlFor="confirmPassword">确认新密码</Label>
                  <PasswordInputContainer>
                    <PasswordInput
                      id="confirmPassword"
                      type={isConfirmPasswordVisible ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="请再次输入新密码"
                      disabled={isSubmitting}
                    />
                    <EyeButton onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}>
                      {isConfirmPasswordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </EyeButton>
                  </PasswordInputContainer>
                  {errors.confirmPassword && <ErrorText>{errors.confirmPassword}</ErrorText>}
                </div>
                
                <ModalActions>
                  <CancelActionButton onClick={handleClosePasswordModal} disabled={isSubmitting}>
                    取消
                  </CancelActionButton>
                  <SubmitActionButton 
                    onClick={handleChangePassword} 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 size={14} className="spin" />
                    ) : (
                      '确认修改'
                    )}
                  </SubmitActionButton>
                </ModalActions>
              </ModalContainer>
            </ModalOverlay>
          )}
          
          {/* 密码修改成功提示弹窗 */}
          <AlertModal
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
            title="密码修改成功"
            message="您的登录密码已成功修改！"
            type="success"
            confirmText="确定"
          />
      </PageContainer>
      
      {toastMessage && (
        <ToastMessage type={toastType}>
          {toastMessage}
        </ToastMessage>
      )}
    </>
  );
};

export default AccountSettingsPage;
